/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import {
  useMarriottAIPreviewStore,
  ConversationSearchCriteria,
  ConversationFilterState,
  ConversationSearchState,
  DEFAULT_SEARCH_CRITERIA,
  DEFAULT_FILTER_STATE,
} from '../store';
import { buildChatAvailabilityUrl } from '../modules/utils/chatAvailabilityUrl';
import { syncConversationStateToGlobalStores } from '../utils/storeSyncUtils';
import { useConditionalPageContextSERP } from './lib/useConditionalPageContext';
import { addSubDirectoryPrefix } from '../utils/subDirectoryFunctions';

/**
 * Hook to get the current conversation's search criteria with shallow comparison.
 * Uses shallow comparison to prevent unnecessary re-renders when object contents are equal.
 *
 * @returns The current conversation's search criteria, or defaults if not set
 */
export const useConversationSearchCriteria = (): ConversationSearchCriteria => {
  return useMarriottAIPreviewStore(state => {
    const chat = state.chatSessions.find(c => c.id === state.currentChatId);
    return chat?.conversationSearchState?.searchCriteria ?? DEFAULT_SEARCH_CRITERIA;
  }, shallow);
};

/**
 * Hook to get the current conversation's filter state with shallow comparison.
 *
 * @returns The current conversation's filter state, or defaults if not set
 */
export const useConversationFilterState = (): ConversationFilterState => {
  return useMarriottAIPreviewStore(state => {
    const chat = state.chatSessions.find(c => c.id === state.currentChatId);
    return chat?.conversationSearchState?.filterState ?? DEFAULT_FILTER_STATE;
  }, shallow);
};

/**
 * Hook to get the current conversation's location state with shallow comparison.
 *
 * @returns The current conversation's location state, or empty object if not set
 */
export const useConversationLocationState = () => {
  return useMarriottAIPreviewStore(state => {
    const chat = state.chatSessions.find(c => c.id === state.currentChatId);
    return chat?.conversationSearchState?.locationState ?? {};
  }, shallow);
};

/**
 * Hook to get the full conversation search state with shallow comparison.
 *
 * @returns The current conversation's full search state, or undefined if not set
 */
export const useConversationSearchState = (): ConversationSearchState | undefined => {
  return useMarriottAIPreviewStore(state => {
    const chat = state.chatSessions.find(c => c.id === state.currentChatId);
    return chat?.conversationSearchState;
  }, shallow);
};

/**
 * Hook to get specific search criteria fields (atomic selectors for primitives).
 * These don't need shallow comparison since they return primitive values.
 */
export const useConversationCheckInDate = (): string | undefined => {
  return useMarriottAIPreviewStore(state => {
    const chat = state.chatSessions.find(c => c.id === state.currentChatId);
    return chat?.conversationSearchState?.searchCriteria?.checkInDate;
  });
};

export const useConversationCheckOutDate = (): string | undefined => {
  return useMarriottAIPreviewStore(state => {
    const chat = state.chatSessions.find(c => c.id === state.currentChatId);
    return chat?.conversationSearchState?.searchCriteria?.checkOutDate;
  });
};

export const useConversationNumRooms = (): number => {
  return useMarriottAIPreviewStore(state => {
    const chat = state.chatSessions.find(c => c.id === state.currentChatId);
    return chat?.conversationSearchState?.searchCriteria?.numRooms ?? 1;
  });
};

export const useConversationUseRewardsPoints = (): boolean => {
  return useMarriottAIPreviewStore(state => {
    const chat = state.chatSessions.find(c => c.id === state.currentChatId);
    return chat?.conversationSearchState?.searchCriteria?.useRewardsPoints ?? false;
  });
};

export const useConversationIncludeTaxesAndFees = (): boolean => {
  return useMarriottAIPreviewStore(state => {
    const chat = state.chatSessions.find(c => c.id === state.currentChatId);
    return chat?.conversationSearchState?.filterState?.includeTaxesAndFees ?? false;
  });
};

/**
 * Hook to get actions for updating conversation search state.
 * Returns memoized callbacks that update the current conversation's state
 * and sync to global stores.
 */
export const useConversationSearchActions = () => {
  const currentChatId = useMarriottAIPreviewStore(state => state.currentChatId);
  const setConversationSearchCriteria = useMarriottAIPreviewStore(state => state.setConversationSearchCriteria);
  const setConversationFilterState = useMarriottAIPreviewStore(state => state.setConversationFilterState);
  const getConversationSearchState = useMarriottAIPreviewStore(state => state.getConversationSearchState);

  const updateSearchCriteria = useCallback(
    (criteria: Partial<ConversationSearchCriteria>) => {
      if (!currentChatId) return;

      // Update the conversation's search criteria
      setConversationSearchCriteria(currentChatId, criteria);

      // Get the updated state and sync to global stores
      const updatedState = getConversationSearchState(currentChatId);
      if (updatedState) {
        syncConversationStateToGlobalStores(currentChatId, updatedState);
      }
    },
    [currentChatId, setConversationSearchCriteria, getConversationSearchState]
  );

  const updateFilterState = useCallback(
    (filters: Partial<ConversationFilterState>) => {
      if (!currentChatId) return;

      // Update the conversation's filter state
      setConversationFilterState(currentChatId, filters);

      // Get the updated state and sync to global stores
      const updatedState = getConversationSearchState(currentChatId);
      if (updatedState) {
        syncConversationStateToGlobalStores(currentChatId, updatedState);
      }
    },
    [currentChatId, setConversationFilterState, getConversationSearchState]
  );

  return {
    updateSearchCriteria,
    updateFilterState,
    currentChatId,
  };
};

/**
 * Hook to build a rates/availability URL for a property using the current conversation's search criteria.
 * Memoized to prevent unnecessary recalculations.
 *
 * @param propertyCode - The property code to build the URL for
 * @param forceRateCalendar - If true, forces the rate calendar view
 * @returns The constructed availability URL
 */
export const useBuildRatesUrl = (propertyCode: string, forceRateCalendar = false): string => {
  const searchCriteria = useConversationSearchCriteria();
  const { NEXT_PUBLIC_AVAILABILITY_SEARCH_URL } = useConditionalPageContextSERP();

  return useMemo(() => {
    const baseUrl = addSubDirectoryPrefix(NEXT_PUBLIC_AVAILABILITY_SEARCH_URL ?? '');

    return buildChatAvailabilityUrl({
      baseUrl,
      propertyCode,
      chatSearchCriteria: {
        checkInDate: searchCriteria.checkInDate,
        checkOutDate: searchCriteria.checkOutDate,
        flexibleDate: searchCriteria.flexible,
        numRooms: searchCriteria.numRooms,
        numAdultsPerRoom: searchCriteria.numAdultsPerRoom,
        numChildrenPerRoom: searchCriteria.numChildrenPerRoom,
        childrenAges: searchCriteria.childrenAges,
        useRewardsPoints: searchCriteria.useRewardsPoints,
        clusterCode: searchCriteria.clusterCode,
        corporateCode: searchCriteria.corporateCode,
        groupCode: searchCriteria.groupCode,
      },
      forceRateCalendar,
    });
  }, [
    propertyCode,
    forceRateCalendar,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    searchCriteria.checkInDate,
    searchCriteria.checkOutDate,
    searchCriteria.flexible,
    searchCriteria.numRooms,
    searchCriteria.numAdultsPerRoom,
    searchCriteria.numChildrenPerRoom,
    searchCriteria.childrenAges,
    searchCriteria.useRewardsPoints,
    searchCriteria.clusterCode,
    searchCriteria.corporateCode,
    searchCriteria.groupCode,
  ]);
};

/**
 * Hook that returns a function to build rates URLs on demand.
 * Useful when you need to build URLs for multiple properties.
 */
export const useBuildRatesUrlFn = () => {
  const searchCriteria = useConversationSearchCriteria();
  const { NEXT_PUBLIC_AVAILABILITY_SEARCH_URL } = useConditionalPageContextSERP();

  return useCallback(
    (propertyCode: string, forceRateCalendar = false): string => {
      const baseUrl = addSubDirectoryPrefix(NEXT_PUBLIC_AVAILABILITY_SEARCH_URL ?? '');

      return buildChatAvailabilityUrl({
        baseUrl,
        propertyCode,
        chatSearchCriteria: {
          checkInDate: searchCriteria.checkInDate,
          checkOutDate: searchCriteria.checkOutDate,
          flexibleDate: searchCriteria.flexible,
          numRooms: searchCriteria.numRooms,
          numAdultsPerRoom: searchCriteria.numAdultsPerRoom,
          numChildrenPerRoom: searchCriteria.numChildrenPerRoom,
          childrenAges: searchCriteria.childrenAges,
          useRewardsPoints: searchCriteria.useRewardsPoints,
          clusterCode: searchCriteria.clusterCode,
          corporateCode: searchCriteria.corporateCode,
          groupCode: searchCriteria.groupCode,
        },
        forceRateCalendar,
      });
    },
    [
      NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      searchCriteria.checkInDate,
      searchCriteria.checkOutDate,
      searchCriteria.flexible,
      searchCriteria.numRooms,
      searchCriteria.numAdultsPerRoom,
      searchCriteria.numChildrenPerRoom,
      searchCriteria.childrenAges,
      searchCriteria.useRewardsPoints,
      searchCriteria.clusterCode,
      searchCriteria.corporateCode,
      searchCriteria.groupCode,
    ]
  );
};
